import { isEmpty } from 'lodash';
import { ENVIRONMENTS, FEATURE_FLAGS, FEATURES, PERMISSION_ACTIONS, PERMISSION_KEYS, TENANT_TYPES } from '../constants';
import { isDarkweb } from './engagement-type-utils';
import { isAdminUser, isCyberintUser, isProducerUser, isCyberintRootUser, isMsspUser } from './user-utils';
import { isDirectMsspTenant } from './tenant-type-utils';

const { READ, WRITE } = PERMISSION_ACTIONS;

/**
 * Function to get permission key
 * @param {object} permissions - dictionary from Backend
 * @param {string} key - permission key without needed action
 * @param {string} action - write or read action
 */
export const hasPermission = ({ permissions, key, action }) => {
  if (!isEmpty(permissions) && key && (action === WRITE || action === READ)) {
    const keyProp = `${key}.${action}`;
    return permissions[keyProp];
  }
  return null;
};

export const isCreateTiAssetAllowed = ({ permissions, features, user }) => {
  if (permissions) {
    return hasPermission({ permissions, key: PERMISSION_KEYS.ASSETS_MGMT, action: WRITE });
  }
  const ALLOWED_FEATURES = [FEATURES.INTELLIGENCE_BASE, FEATURES.ASSET_MGMT_CREATE];
  const isAdmin = isAdminUser(user);
  const hasTiFeature = ALLOWED_FEATURES.every(f => features?.includes(f));

  const isCyberintProducerOrAdmin = isCyberintUser(user) && (isAdmin || isProducerUser(user));
  return isCyberintProducerOrAdmin || (isAdmin && hasTiFeature);
};

export const isLicenseVisibilityAllowed = ({ permissions }) =>
  hasPermission({ permissions, key: PERMISSION_KEYS.LICENSE, action: READ });

export const isAssetReadAllowed = ({ customer, user }) => {
  if (isCyberintRootUser(user)) return true;
  return TENANT_TYPES.CYBERINT_MANAGEMENT !== customer?.tenant_type;
};

export const isTemAllowed = ({ features }) => features?.includes(FEATURES.TEM_ACCESS);

export const isCveIntelligenceAllowed = ({ features }) => features?.includes(FEATURES.CVE_ACCESS);

export const isAccountSettingsAllowed = ({ user, features, selectedTenant }) => {
  /** In case the user in not Cyberint, and selected tenant is Argos Demo, but not logged in as Argos Demo - we hide thee route */
  if (
    !isCyberintUser(user) &&
    selectedTenant?._id === ENVIRONMENTS.ARGOS_DEMO_CUSTOMER_ID &&
    user?.customer?._id !== selectedTenant?._id
  ) {
    return false;
  }
  return isCyberintRootUser(user) || features?.includes(FEATURES.MY_ACCOUNT_FEATURE);
};

export const isFilterNavMenu = ({ selectedCustomer, user }) =>
  (selectedCustomer?.id || selectedCustomer) === ENVIRONMENTS.ARGOS_DEMO_CUSTOMER_ID && isCyberintRootUser(user);

export const isASMVisible = ({ user, selectedTenant: tenant, features, isCyberintRoot }) => {
  const isRoot = isCyberintRootUser(user) || isCyberintRoot;

  if (isRoot) return true;
  if (isDarkweb(tenant)) return false;
  return (features || tenant?.features)?.includes(FEATURES.ASM_FEATURE_VIEW);
};

export const isAssetGroupingAllowed = ({ features }) => {
  return features?.includes(FEATURES.ASSET_GROUPING_VIEW);
};

export const isDrpAllowed = ({ features }) => features?.some(f => FEATURES.DRP_FEATURES.includes(f));

export const isOverrideValidationAllowed = ({ user, customer }) =>
  isAdminUser(user) && (isCyberintUser(user) || isMsspUser(user) || isDirectMsspTenant(customer));

export const isQuantumIntegrationVisible = ({ features }) => {
  return features?.includes(FEATURES.CP_QUANTUM_INTEGRATION);
};

export const isQuantumIntegrationManageAllowed = ({ features }) => {
  return features?.includes(FEATURES.QUANTUM_INTEGRATION_MANAGE);
};

export const isAevEnabled = ({ features }) => {
  return features?.includes(FEATURES.ATTACK_SURFACE_MONITORING_ACTIVE_VULNERABILITY_SCAN);
};

export const isSavedQueryCapacityAllowed = ({ permissions }) =>
  hasPermission({ permissions, key: PERMISSION_KEYS.TI__SAVED_QUERY_CAPACITY, action: READ });

export const isCyberNewsVisible = ({ flags }) => flags?.[FEATURE_FLAGS.CYBER_NEWS];

export const isAssetMgmtNg = ({ flags }) => flags?.[FEATURE_FLAGS.ASSET_MGMT];
export const isThreatReadinessDashboardVisible = ({ flags }) =>
  flags?.[FEATURE_FLAGS.TI_THREAT_READINESS_DASHBOARD_ENABLED];
